import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    getDocs,
    orderBy,
    doc,
    setDoc,
    onSnapshot,
    getDoc,
    updateDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../../Services/firebase-config';
import { useNavigate } from 'react-router-dom';
import {
    CollectionIcon,
    EyeIcon,
    SearchIcon,
    PlusIcon,
    XIcon,
    UploadIcon,
    SaveIcon,
    TrashIcon
} from '@heroicons/react/outline';
import { Trophy, TrendingUp, ToggleLeft, ToggleRight } from 'lucide-react';

const ProjectsManagement = () => {
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [projectsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [categoryStats, setCategoryStats] = useState([]);
    const [totalVotes, setTotalVotes] = useState(0);
    const [canVote, setCanVote] = useState(false);
    const [votingConfigLoading, setVotingConfigLoading] = useState(false);
    const [successNotification, setSuccessNotification] = useState({
        show: false,
        message: ''
    });
    const [newProject, setNewProject] = useState({
        id: '',
        category: '',
        exhibitorNames: [],
        institution: '',
        innovationDescription: '',
        image: ''
    });
    const navigate = useNavigate();

    // Function to show success notification
    const showSuccessNotification = (message) => {
        setSuccessNotification({
            show: true,
            message
        });

        // Auto-hide after 4 seconds
        setTimeout(() => {
            setSuccessNotification({
                show: false,
                message: ''
            });
        }, 4000);
    };

    useEffect(() => {
        fetchProjects();
        fetchVotingConfig();
    }, []);

    useEffect(() => {
        // Filter projects based on search term
        if (searchTerm.trim() === '') {
            setFilteredProjects(projects);
        } else {
            const filtered = projects.filter(project =>
                project.exhibitorNames?.some(name =>
                    name.toLowerCase().includes(searchTerm.toLowerCase())
                ) ||
                project.institution?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.innovationDescription?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredProjects(filtered);
        }
        setCurrentPage(1); // Reset to first page when searching
    }, [searchTerm, projects]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const projectsRef = collection(db, 'projects');
            const q = query(projectsRef, orderBy('category', 'asc'));
            const querySnapshot = await getDocs(q);

            const projectsData = [];
            querySnapshot.forEach((doc) => {
                projectsData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            setProjects(projectsData);
            setFilteredProjects(projectsData);
        } catch (error) {
            console.error('Error fetching projects:', error);
        } finally {
            setLoading(false);
        }
    };

    // Real-time listener for vote counts from Firebase
    const setupVoteListener = useCallback(() => {
        try {
            const votesRef = collection(db, 'votes');

            // Set up real-time listener
            const unsubscribe = onSnapshot(votesRef, async (querySnapshot) => {
                const counts = {};

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const applicationId = data.applicationId;
                    if (counts[applicationId]) {
                        counts[applicationId]++;
                    } else {
                        counts[applicationId] = 1;
                    }
                });

                // Calculate category stats
                const categoryVotes = {};
                let totalVotesCount = 0;

                projects.forEach((project) => {
                    const voteCount = counts[project.id] || 0;
                    totalVotesCount += voteCount;

                    const category = project.category || 'Uncategorized';
                    if (!categoryVotes[category]) {
                        categoryVotes[category] = {
                            votes: 0,
                            leadingProject: null,
                            maxVotes: 0
                        };
                    }

                    categoryVotes[category].votes += voteCount;

                    // Track leading project in this category
                    if (voteCount > categoryVotes[category].maxVotes) {
                        categoryVotes[category].maxVotes = voteCount;
                        categoryVotes[category].leadingProject = project;
                    }
                });

                // Convert to array and sort by votes
                const categoryStatsArray = Object.entries(categoryVotes)
                    .map(([category, data]) => ({
                        category,
                        votes: data.votes,
                        leadingProject: data.leadingProject,
                        leadingVotes: data.maxVotes
                    }))
                    .sort((a, b) => b.votes - a.votes);

                setCategoryStats(categoryStatsArray);
                setTotalVotes(totalVotesCount);
            }, (error) => {
                console.error('Error listening to vote updates:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up vote listener:', error);
            return null;
        }
    }, [projects]);

    // Fetch voting configuration from Firebase
    const fetchVotingConfig = async () => {
        try {
            const configRef = doc(db, 'configs', 'voting');
            const configSnapshot = await getDoc(configRef);

            if (configSnapshot.exists()) {
                const data = configSnapshot.data();
                setCanVote(data.canVote || false);
            } else {
                // If document doesn't exist, create it with default value
                await setDoc(configRef, { canVote: false });
                setCanVote(false);
            }
        } catch (error) {
            console.error('Error fetching voting config:', error);
        }
    };

    // Toggle voting status
    const toggleVotingStatus = async () => {
        try {
            setVotingConfigLoading(true);
            const newVotingStatus = !canVote;

            const configRef = doc(db, 'configs', 'voting');
            await updateDoc(configRef, { canVote: newVotingStatus });

            setCanVote(newVotingStatus);

            // Show success notification instead of alert
            showSuccessNotification(`Voting has been ${newVotingStatus ? 'enabled' : 'disabled'} successfully!`);
        } catch (error) {
            console.error('Error updating voting config:', error);
            alert('Error updating voting configuration. Please try again.');
        } finally {
            setVotingConfigLoading(false);
        }
    };

    // Set up real-time vote listener
    useEffect(() => {
        let unsubscribe = null;

        const initializeListener = async () => {
            // First load the projects
            await fetchProjects();
        };

        initializeListener();

        // Cleanup listener on unmount
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, []);

    // Set up vote listener when projects change
    useEffect(() => {
        let unsubscribe = null;

        if (projects.length > 0) {
            unsubscribe = setupVoteListener();
        }

        // Cleanup listener on unmount or when projects change
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [projects, setupVoteListener]);

    const getUniqueCategories = () => {
        const categories = projects.map(project => project.category).filter(Boolean);
        return [...new Set(categories)];
    };

    const getExhibitorNames = (exhibitorNames) => {
        if (!exhibitorNames || !Array.isArray(exhibitorNames)) return 'N/A';
        if (exhibitorNames.length <= 2) {
            return exhibitorNames.join(', ');
        }
        return `${exhibitorNames.slice(0, 2).join(', ')} +${exhibitorNames.length - 2} more`;
    };

    // Pagination logic
    const indexOfLastProject = currentPage * projectsPerPage;
    const indexOfFirstProject = indexOfLastProject - projectsPerPage;
    const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
    const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const resetNewProject = () => {
        setNewProject({
            id: '',
            category: '',
            exhibitorNames: [],
            institution: '',
            innovationDescription: '',
            image: ''
        });
        setSelectedFile(null);
    };

    const handleAddProject = () => {
        resetNewProject();
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        resetNewProject();
    };

    const handleInputChange = (field, value) => {
        setNewProject(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExhibitorNamesChange = (value) => {
        const namesArray = value.split(',').map(name => name.trim()).filter(name => name);
        setNewProject(prev => ({
            ...prev,
            exhibitorNames: namesArray
        }));
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleImageUpload = async () => {
        if (!selectedFile) return null;

        try {
            setUploading(true);
            const timestamp = Date.now();
            const filename = `images/${timestamp}_${selectedFile.name}`;
            const storageRef = ref(storage, filename);

            const snapshot = await uploadBytes(storageRef, selectedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);

            setNewProject(prev => ({
                ...prev,
                image: downloadURL
            }));

            return downloadURL;
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Error uploading image. Please try again.');
            return null;
        } finally {
            setUploading(false);
        }
    };

    const generateProjectId = () => {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000);
        return `proj-${timestamp}-${random}`;
    };

    const handleSaveProject = async () => {
        // Validate required fields
        if (!newProject.category || !newProject.institution || !newProject.innovationDescription) {
            alert('Please fill in all required fields (Category, Institution, Innovation Description)');
            return;
        }

        if (newProject.exhibitorNames.length === 0) {
            alert('Please add at least one exhibitor name');
            return;
        }

        try {
            setSaving(true);

            // Upload image if selected
            let imageUrl = newProject.image;
            if (selectedFile && !imageUrl) {
                imageUrl = await handleImageUpload();
                if (!imageUrl) return; // Upload failed
            }

            // Generate project ID
            const projectId = newProject.id || generateProjectId();

            // Create project document
            const projectData = {
                category: newProject.category,
                exhibitorNames: newProject.exhibitorNames,
                institution: newProject.institution,
                innovationDescription: newProject.innovationDescription,
                image: imageUrl || '',
                createdAt: new Date()
            };

            // Save to Firestore with custom ID
            await setDoc(doc(db, 'projects', projectId), projectData);

            // Refresh projects list
            await fetchProjects();

            // Close modal and reset
            handleCloseModal();
            showSuccessNotification('Project created successfully!');

        } catch (error) {
            console.error('Error creating project:', error);
            alert('Error creating project. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Success Notification */}
            {successNotification.show && (
                <div className="fixed top-4 right-4 z-50 transform transition-all duration-500 ease-out animate-bounce">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-lg shadow-xl border border-green-400 flex items-center gap-3 min-w-[320px] max-w-md">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-sm">{successNotification.message}</p>
                        </div>
                        <button
                            onClick={() => setSuccessNotification({ show: false, message: '' })}
                            className="flex-shrink-0 text-green-200 hover:text-white transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Projects Management</h1>
                        <p className="text-gray-600">Manage innovation projects and exhibitor information</p>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Voting Toggle */}
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Voting:</span>
                            <button
                                onClick={toggleVotingStatus}
                                disabled={votingConfigLoading}
                                className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${canVote
                                    ? 'bg-green-100 text-green-800 border-2 border-green-200 hover:bg-green-200'
                                    : 'bg-red-100 text-red-800 border-2 border-red-200 hover:bg-red-200'
                                    } ${votingConfigLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                {votingConfigLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                ) : (
                                    <>
                                        {canVote ? (
                                            <>
                                                <ToggleRight className="h-5 w-5 text-green-600" />
                                                <span>Enabled</span>
                                            </>
                                        ) : (
                                            <>
                                                <ToggleLeft className="h-5 w-5 text-red-600" />
                                                <span>Disabled</span>
                                            </>
                                        )}
                                    </>
                                )}
                            </button>
                        </div>

                        <button
                            onClick={handleAddProject}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-custom-blue-1 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Add Project
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Projects</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">{projects.length}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50">
                            <CollectionIcon className="h-8 w-8 text-custom-blue-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Votes</p>
                            <p className="text-3xl font-bold text-custom-green-1 mt-2">{totalVotes}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50">
                            <TrendingUp className="h-8 w-8 text-custom-green-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Categories</p>
                            <p className="text-3xl font-bold text-purple-600 mt-2">
                                {getUniqueCategories().length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-50">
                            <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Exhibitors</p>
                            <p className="text-3xl font-bold text-orange-500 mt-2">
                                {projects.reduce((sum, project) => sum + (project.exhibitorNames?.length || 0), 0)}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50">
                            <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Vote Statistics */}
            {categoryStats && categoryStats.length > 0 && (
                <div className="mb-6 bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Trophy className="w-5 h-5 mr-2 text-gray-600" />
                        Category Vote Leaders
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {categoryStats.map((categoryData, index) => (
                            <div
                                key={categoryData.category}
                                className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate" title={categoryData.category}>
                                            {categoryData.category}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {categoryData.votes} total vote{categoryData.votes !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    <div className="ml-2 flex-shrink-0">
                                        {index === 0 && categoryData.votes > 0 && (
                                            <div className="flex items-center justify-center w-6 h-6 bg-yellow-100 rounded-full">
                                                <span className="text-yellow-600 text-xs">👑</span>
                                            </div>
                                        )}
                                        {index === 1 && categoryData.votes > 0 && (
                                            <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full">
                                                <span className="text-gray-600 text-xs">🥈</span>
                                            </div>
                                        )}
                                        {index === 2 && categoryData.votes > 0 && (
                                            <div className="flex items-center justify-center w-6 h-6 bg-orange-100 rounded-full">
                                                <span className="text-orange-600 text-xs">🥉</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Leading Project Info */}
                                {categoryData.leadingProject && categoryData.leadingVotes > 0 && (
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Trophy className="w-4 h-4 text-blue-600" />
                                            <span className="text-xs font-medium text-blue-900">Category Leader</span>
                                        </div>
                                        <p className="text-sm font-semibold text-blue-900 truncate" title={categoryData.leadingProject.institution}>
                                            {categoryData.leadingProject.institution}
                                        </p>
                                        <p className="text-xs text-blue-700 mt-1">
                                            {categoryData.leadingVotes} vote{categoryData.leadingVotes !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                )}

                                {/* Progress bar */}
                                <div className="mt-2">
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className="bg-custom-blue-1 h-1.5 rounded-full transition-all duration-300"
                                            style={{
                                                width: totalVotes > 0
                                                    ? `${(categoryData.votes / totalVotes) * 100}%`
                                                    : '0%'
                                            }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <span className="text-xs text-gray-500">
                                            {totalVotes > 0 ? `${Math.round((categoryData.votes / totalVotes) * 100)}%` : '0%'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            of total votes
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Search Bar */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                        placeholder="Search projects by exhibitor names, institution, category, or description..."
                    />
                </div>
                {searchTerm && (
                    <div className="mt-2 text-sm text-gray-600">
                        Found {filteredProjects.length} project(s) matching "{searchTerm}"
                    </div>
                )}
            </div>

            {/* Projects Table */}
            <div className="bg-white shadow-sm overflow-hidden rounded-lg border">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Project Details
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Exhibitors
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Category
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Institution
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentProjects.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        {searchTerm ? 'No projects found matching your search criteria' : 'No projects found'}
                                    </td>
                                </tr>
                            ) : (
                                currentProjects.map((project) => (
                                    <tr key={project.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900 mb-1">
                                                    Project ID: {project.id}
                                                </div>
                                                <div className="text-sm text-gray-500 max-w-xs truncate">
                                                    {project.innovationDescription}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {getExhibitorNames(project.exhibitorNames)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {project.category || 'Uncategorized'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {project.institution || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => navigate(`/eventdashboard/project-details/${project.id}`, {
                                                    state: { project }
                                                })}
                                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <EyeIcon className="h-4 w-4 mr-1" />
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                let pageNumber;
                                if (totalPages <= 7) {
                                    pageNumber = i + 1;
                                } else if (currentPage <= 4) {
                                    pageNumber = i + 1;
                                } else if (currentPage >= totalPages - 3) {
                                    pageNumber = totalPages - 6 + i;
                                } else {
                                    pageNumber = currentPage - 3 + i;
                                }

                                if (pageNumber < 1 || pageNumber > totalPages) return null;

                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${pageNumber === currentPage
                                            ? 'bg-custom-blue-1 text-white'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>

                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages} • Showing {indexOfFirstProject + 1}-{Math.min(indexOfLastProject, filteredProjects.length)} of {filteredProjects.length} projects
                    </div>
                </div>
            )}

            {/* Add Project Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Add New Project</h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Form */}
                            <div className="space-y-4">
                                {/* Project ID */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Project ID (Optional - will be auto-generated if empty)
                                    </label>
                                    <input
                                        type="text"
                                        value={newProject.id}
                                        onChange={(e) => handleInputChange('id', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        placeholder="proj-001"
                                    />
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={newProject.category}
                                        onChange={(e) => handleInputChange('category', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        required
                                    >
                                        <option value="">Select a category</option>
                                        <option value="Aeronautics and Space Science">Aeronautics and Space Science</option>
                                        <option value="Industry 4.0+ (Electronics)">Industry 4.0+ (Electronics)</option>
                                        <option value="Industry 4.0+ (Software)">Industry 4.0+ (Software)</option>
                                        <option value="Infrastructure Innovations">Infrastructure Innovations</option>
                                        <option value="Infrastructure Innovations (Materials)">Infrastructure Innovations (Materials)</option>
                                        <option value="Infrastructure Innovations (Mechanical)">Infrastructure Innovations (Mechanical)</option>
                                        <option value="Infrastructure Innovations(Structures)">Infrastructure Innovations(Structures)</option>
                                        <option value="Mobility">Mobility</option>
                                    </select>
                                </div>

                                {/* Institution */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Institution <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newProject.institution}
                                        onChange={(e) => handleInputChange('institution', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        placeholder="Innovex Uganda"
                                        required
                                    />
                                </div>

                                {/* Exhibitor Names */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Exhibitor Names (comma-separated) <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={newProject.exhibitorNames.join(', ')}
                                        onChange={(e) => handleExhibitorNamesChange(e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        placeholder="Maria Imelda Namatovu, Anold Asiimwe"
                                        required
                                    />
                                </div>

                                {/* Innovation Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Innovation Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={newProject.innovationDescription}
                                        onChange={(e) => handleInputChange('innovationDescription', e.target.value)}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        placeholder="Describe the innovation project..."
                                        required
                                    />
                                </div>

                                {/* Image Upload */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Project Image
                                    </label>

                                    {/* Current Image Preview */}
                                    {newProject.image && (
                                        <div className="mb-3 relative">
                                            <img
                                                src={newProject.image}
                                                alt="Project preview"
                                                className="w-full h-32 object-cover rounded-md border"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            <button
                                                onClick={() => handleInputChange('image', '')}
                                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                                title="Remove image"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}

                                    {/* File Upload */}
                                    <div className="space-y-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileSelect}
                                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-custom-blue-1 hover:file:bg-blue-100"
                                            />
                                            {selectedFile && (
                                                <button
                                                    onClick={handleImageUpload}
                                                    disabled={uploading}
                                                    className="px-3 py-2 bg-custom-blue-1 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                                >
                                                    {uploading ? (
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                    ) : (
                                                        <UploadIcon className="h-4 w-4 mr-1" />
                                                    )}
                                                    {uploading ? 'Uploading...' : 'Upload'}
                                                </button>
                                            )}
                                        </div>
                                        {selectedFile && (
                                            <p className="text-sm text-gray-600">
                                                Selected: {selectedFile.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Manual URL Input */}
                                    <div className="mt-3">
                                        <input
                                            type="url"
                                            value={newProject.image}
                                            onChange={(e) => handleInputChange('image', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                            placeholder="Or enter image URL"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveProject}
                                    disabled={saving || uploading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-custom-blue-1 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1 disabled:opacity-50 flex items-center"
                                >
                                    {saving ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    ) : (
                                        <SaveIcon className="h-4 w-4 mr-2" />
                                    )}
                                    {saving ? 'Creating...' : 'Create Project'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsManagement;
